import { findBranchesByLocation } from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import { SearchApiBranch } from '@/app/(main)/(pages)/_types/search-api-response.type';
import { SearchType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { FormatAddressParams } from '@/utils/formatters/format-address';
import { getLogger } from '@/logger/logger';
import { slugify } from '@/utils/formatters/slugfy';
import { minResultsToHideSuggestions } from '@/utils/consts/search-schedules.consts';

export const CITY_BRANCH_SUGGESTION_LIMIT = 2;

export type CityBranch = {
  id: string;
  name: string;
  slug: string | null;
  avatar: string | null;
  whatsapp: string | null;
  distance: number | null;
  address: FormatAddressParams;
};

export type CityBranches = {
  branches: CityBranch[];
  count: number;
};

export type GetCityBranchesParams = {
  searchType: string;
  city?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
};

export function hasRenderableCityBranchSuggestions(
  cityBranches: CityBranches | null,
): cityBranches is CityBranches {
  return Boolean(cityBranches?.branches?.length);
}

function mapToCityBranch(branch: SearchApiBranch): CityBranch {
  return {
    id: branch.id,
    name: branch.name,
    slug: branch.slug ?? null,
    avatar: branch.avatar ?? null,
    whatsapp: branch.whatsapps?.[0] ?? null,
    distance: branch.distance ?? null,
    address: {
      addressLine1: branch.addressLine1 ?? undefined,
      addressLine2: branch.addressLine2 ?? undefined,
      number: branch.number ?? undefined,
      neighborhood: branch.neighborhood ?? undefined,
      city: branch.city ?? undefined,
      state: branch.state ?? undefined,
      zipcode: branch.zipcode ?? undefined,
    },
  };
}

export async function getCityBranches(params: GetCityBranchesParams): Promise<CityBranches | null> {
  if (params.searchType === SearchType.ONLINE_CONSULTATION) {
    return null;
  }

  const logger = getLogger('search.city-branches');

  try {
    const { branches } = await findBranchesByLocation({
      city: params.city
        ? slugify(params.state ? `${params.city}-${params.state}` : params.city)
        : undefined,
      latitude: params.latitude,
      longitude: params.longitude,
      limit: minResultsToHideSuggestions,
    });

    if (!branches.length) {
      return null;
    }

    return {
      branches: branches.slice(0, CITY_BRANCH_SUGGESTION_LIMIT).map(mapToCityBranch),
      count: branches.length,
    };
  } catch (error) {
    logger.error('Failed to fetch city branch suggestions', {
      search: { event: 'city_branches_failed', city: params.city },
      error,
    });

    return null;
  }
}
